import { Flex } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { spacing } from "#app/theme/v2/spacing";
import { useCompany } from "#modules/company/view/useCompany";
import { OnboardingActivityCard } from "#modules/onboardingV2/view/OnboardingActivityCard";
import { OnboardingV2Shell } from "#modules/onboardingV2/view/OnboardingV2Shell";
import { useTrackOnboardingStepViewed } from "#modules/onboardingV2/view/useOnboardingAnalytics";
import { useOnboardingStepNavigation } from "#modules/onboardingV2/view/useOnboardingStepNavigation";
import { useUpdateOnboardingActivityType } from "#modules/onboardingV2/view/useUpdateOnboardingActivityType";
import { IconName } from "#shared/icons/Icon";
import { OnboardingActivityTypeType } from "shared";

const ACTIVITY_TYPES: {
  value: OnboardingActivityTypeType;
  translationKey: "sheet" | "boilermaking" | "machining";
  icon: IconName;
}[] = [
  { value: "SHEET", translationKey: "sheet", icon: "layer" },
  { value: "BOILERMAKING", translationKey: "boilermaking", icon: "cylinder" },
  { value: "MACHINING", translationKey: "machining", icon: "machining" },
];

export const OnboardingActivityTypeStepPage = () => {
  const { t } = useTranslation();
  const { company } = useCompany();
  const [selected, setSelected] = useState<OnboardingActivityTypeType | null>(
    company?.onboardingActivityType ?? null,
  );
  const { updateOnboardingActivityType } = useUpdateOnboardingActivityType();
  const { position, total, goToNext, goToPrevious } = useOnboardingStepNavigation("activityType");

  useTrackOnboardingStepViewed("activityType");

  const handleContinue = () => {
    if (selected === null) {
      return;
    }

    if (selected !== company?.onboardingActivityType) {
      updateOnboardingActivityType(selected);
    }

    goToNext();
  };

  return (
    <OnboardingV2Shell>
      <OnboardingV2Shell.Header
        onBack={goToPrevious}
        step={position}
        total={total}
        title={t("onboardingV2.activityType.title")}
      />

      <OnboardingV2Shell.Content>
        <Flex gap={spacing[6]} align="stretch">
          {ACTIVITY_TYPES.map((activityType) => (
            <OnboardingActivityCard
              key={activityType.value}
              icon={activityType.icon}
              title={t(`onboardingV2.activityType.options.${activityType.translationKey}.title`)}
              description={t(
                `onboardingV2.activityType.options.${activityType.translationKey}.description`,
              )}
              selected={selected === activityType.value}
              onClick={() => setSelected(activityType.value)}
            />
          ))}
        </Flex>
      </OnboardingV2Shell.Content>

      <OnboardingV2Shell.Footer
        buttonLabel={t("onboardingV2.continue")}
        onContinue={handleContinue}
        disabled={selected === null}
        stepper={{ total, current: position }}
      />
    </OnboardingV2Shell>
  );
};
